import React, { useState, useRef, useEffect, Fragment, useCallback } from 'react';
import { Box, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

const CanvasWrapper = styled(Box)(() => ({
  width: '960px',
  height: '540px',
  margin: '0 auto',
  overflow: 'auto', // Add scrollbar if needed
}));

const CanvasContainer = styled(Paper)(() => ({
  position: 'relative',
  width: '960px',
  height: '540px',
  backgroundColor: '#000',
  overflow: 'hidden',
  cursor: 'crosshair',
}));

const CanvasContent = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23333' fill-opacity='0.1'%3E%3Cpath d='M0 0h20v20H0z'/%3E%3Cpath d='M20 20h20v20H20z'/%3E%3C/g%3E%3C/svg%3E")`,
  backgroundSize: '40px 40px',
});

const WatermarkText = styled(Box)(() => ({
  position: 'absolute',
  userSelect: 'none',
  // Remove transition during drag to prevent visual artifacts
}));

const SafeAreaGuide = styled(Box)(() => ({
  position: 'absolute',
  border: '1px dashed',
  borderColor: 'rgba(255, 255, 255, 0.3)',
  pointerEvents: 'none',
  '&::before': {
    content: 'attr(data-label)',
    position: 'absolute',
    top: -20,
    left: 0,
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.5)',
    fontFamily: 'Arial',
  },
}));

const SnapGuide = styled(Box)(({ theme }) => ({
  position: 'absolute',
  pointerEvents: 'none',
  backgroundColor: theme.palette.primary.main,
  opacity: 0.5,
  transition: 'opacity 0.2s',
}));

const SNAP_THRESHOLD = 5; // pixels
const SNAP_STICKINESS = 8; // pixels - how far to drag before breaking snap

function WatermarkCanvas({ 
  elements, 
  onElementUpdate, 
  selectedElement, 
  onSelectElement,
  previewText = "John Doe - ABC123\nProperty of Sample Productions",
  showSafeAreas = true
}) {
  const containerRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    elementId: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    initialX: 0,
    initialY: 0,
    snappedToX: null,
    snappedToY: null,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [snapGuides, setSnapGuides] = useState({
    vertical: [],
    horizontal: [],
  });
  const snapGuidesRef = useRef({ vertical: [], horizontal: [] });
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        // Update size if needed
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const getSafeAreaBounds = () => {
    return {
      titleSafe: {
        left: 5,
        right: 95,
        top: 5,
        bottom: 95,
      },
      actionSafe: {
        left: 10,
        right: 90,
        top: 10,
        bottom: 90,
      },
    };
  };

  const getSnapPoints = (elementId, _x, _y) => {
    const snapPoints = {
      vertical: [],
      horizontal: [],
    };
    
    const bounds = getSafeAreaBounds();
    
    // Add safe area snap points
    snapPoints.vertical.push(
      { position: bounds.titleSafe.left, type: 'titleSafe', label: 'Title Safe Left' },
      { position: bounds.titleSafe.right, type: 'titleSafe', label: 'Title Safe Right' },
      { position: bounds.actionSafe.left, type: 'actionSafe', label: 'Action Safe Left' },
      { position: bounds.actionSafe.right, type: 'actionSafe', label: 'Action Safe Right' },
      { position: 50, type: 'center', label: 'Center' }
    );
    
    snapPoints.horizontal.push(
      { position: bounds.titleSafe.top, type: 'titleSafe', label: 'Title Safe Top' },
      { position: bounds.titleSafe.bottom, type: 'titleSafe', label: 'Title Safe Bottom' },
      { position: bounds.actionSafe.top, type: 'actionSafe', label: 'Action Safe Top' },
      { position: bounds.actionSafe.bottom, type: 'actionSafe', label: 'Action Safe Bottom' },
      { position: 50, type: 'center', label: 'Center' }
    );
    
    // Add other element snap points
    elements.forEach(element => {
      if (element.id !== elementId && element.visible) {
        // Element edges and center
        snapPoints.vertical.push(
          { position: element.x, type: 'element', label: `${element.name || 'Element'} Center` }
        );
        snapPoints.horizontal.push(
          { position: element.y, type: 'element', label: `${element.name || 'Element'} Center` }
        );
      }
    });
    
    return snapPoints;
  };

  const findSnapPosition = (value, snapPoints, threshold) => {
    for (const point of snapPoints) {
      if (Math.abs(value - point.position) < threshold) {
        return point;
      }
    }
    return null;
  };

  const handleMouseDown = (e, elementId) => {
    // While editing text, let the input handle clicks normally — no drag.
    if (editingId === elementId) return;
    e.preventDefault();
    e.stopPropagation();
    // Always select the element on mouse down so clicking the layer's
    // visual representation selects it even when no drag occurs.
    onSelectElement(elementId);
    const rect = containerRef.current.getBoundingClientRect();
    const element = elements.find(el => el.id === elementId);
    const textElement = e.currentTarget.querySelector('span');
    const _textRect = textElement?.getBoundingClientRect();
    
    // Calculate the actual position based on text alignment
    let actualX = (element.x / 100) * rect.width;
    // Text alignment is handled by the transform translate values
    
    dragStateRef.current = {
      isDragging: true,
      elementId,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: actualX,
      offsetY: (element.y / 100) * rect.height,
      initialX: element.x,
      initialY: element.y,
      snappedToX: null,
      snappedToY: null,
    };
    
    setIsDragging(true);
    onSelectElement(elementId);
  };

  // Store onElementUpdate in a ref to avoid dependency issues
  const onElementUpdateRef = useRef(onElementUpdate);
  useEffect(() => {
    onElementUpdateRef.current = onElementUpdate;
  }, [onElementUpdate]);

  const handleMouseMove = useCallback((e) => {
    const dragState = dragStateRef.current;
    if (!dragState.isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;
    
    let newX = ((dragState.offsetX + deltaX) / rect.width) * 100;
    let newY = ((dragState.offsetY + deltaY) / rect.height) * 100;
    
    // Get snap points
    const snapPoints = getSnapPoints(dragState.elementId, newX, newY);
    const guides = { vertical: [], horizontal: [] };
    
    // Convert pixel thresholds to percentage based on canvas size
    const xThresholdPercent = ((dragState.snappedToX ? SNAP_STICKINESS : SNAP_THRESHOLD) / rect.width) * 100;
    const yThresholdPercent = ((dragState.snappedToY ? SNAP_STICKINESS : SNAP_THRESHOLD) / rect.height) * 100;
    
    const snapX = findSnapPosition(newX, snapPoints.vertical, xThresholdPercent);
    const snapY = findSnapPosition(newY, snapPoints.horizontal, yThresholdPercent);
    
    if (snapX) {
      newX = snapX.position;
      guides.vertical.push(snapX.position);
      dragStateRef.current.snappedToX = snapX.position;
    } else {
      dragStateRef.current.snappedToX = null;
    }
    
    if (snapY) {
      newY = snapY.position;
      guides.horizontal.push(snapY.position);
      dragStateRef.current.snappedToY = snapY.position;
    } else {
      dragStateRef.current.snappedToY = null;
    }
    
    // Constrain to canvas bounds
    newX = Math.max(0, Math.min(100, newX));
    newY = Math.max(0, Math.min(100, newY));
    
    // Update snap guides ref immediately for visual feedback
    snapGuidesRef.current = guides;
    
    // Throttle state updates using requestAnimationFrame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(() => {
      setSnapGuides(snapGuidesRef.current);
    });
    
    onElementUpdateRef.current(dragState.elementId, {
      x: newX,
      y: newY,
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    dragStateRef.current = {
      isDragging: false,
      elementId: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      initialX: 0,
      initialY: 0,
      snappedToX: null,
      snappedToY: null,
    };
    setIsDragging(false);
    setSnapGuides({ vertical: [], horizontal: [] });
  }, []);

  const handleCanvasClick = (e) => {
    // Only deselect when the click is on the empty canvas itself, not on an
    // element layer (those stop propagation in handleMouseDown).
    if (e.target === e.currentTarget && !dragStateRef.current.isDragging) {
      onSelectElement(null);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <CanvasWrapper>
      <CanvasContainer elevation={2}>
        <CanvasContent 
          ref={containerRef}
          className="canvas-content"
          onClick={handleCanvasClick}
        >
        {/* Video preview placeholder */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#666',
            fontSize: '24px',
            fontWeight: 'bold',
            textAlign: 'center',
          }}
        >
          VIDEO PREVIEW
        </Box>
        
        {/* Title Safe Area (90% of frame) */}
        {showSafeAreas && (
          <SafeAreaGuide
            data-label="Title Safe"
            sx={{
              top: '5%',
              left: '5%',
              width: '90%',
              height: '90%',
            }}
          />
        )}
        
        {/* Action Safe Area (80% of frame) */}
        {showSafeAreas && (
          <SafeAreaGuide
            data-label="Action Safe"
            sx={{
              top: '10%',
              left: '10%',
              width: '80%',
              height: '80%',
              borderColor: 'rgba(255, 255, 255, 0.2)',
            }}
          />
        )}
        
        {/* Snap guides */}
        {snapGuides.vertical.map((position, index) => (
          <SnapGuide
            key={`v-${index}`}
            sx={{
              left: `${position}%`,
              top: 0,
              width: '1px',
              height: '100%',
            }}
          />
        ))}
        {snapGuides.horizontal.map((position, index) => (
          <SnapGuide
            key={`h-${index}`}
            sx={{
              left: 0,
              top: `${position}%`,
              width: '100%',
              height: '1px',
            }}
          />
        ))}
        
        
        {/* Render watermark elements */}
        {elements.map((element) => {
          const isDragging = dragStateRef.current.isDragging && dragStateRef.current.elementId === element.id;
          const isSelected = selectedElement === element.id;
          return (
            <React.Fragment key={element.id}>
              {/* X coordinate indicator line for selected element */}
              {isSelected && element.visible && (
                <>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${element.x}%`,
                      top: 0,
                      width: '1px',
                      height: '100%',
                      backgroundColor: '#1976d2',
                      opacity: 0.3,
                      pointerEvents: 'none',
                      zIndex: 20,
                    }}
                  />
                  {/* Y coordinate indicator line */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: `${element.y}%`,
                      width: '100%',
                      height: '1px',
                      backgroundColor: '#1976d2',
                      opacity: 0.3,
                      pointerEvents: 'none',
                      zIndex: 20,
                    }}
                  />
                </>
              )}
              <WatermarkText
                style={{
                  position: 'absolute',
                  left: `${element.x}%`,
                  top: `${element.y}%`,
                  transform: `translate(${
                    (element.textAlign || 'center') === 'left' ? '0' : 
                    (element.textAlign || 'center') === 'right' ? '-100' : '-50'
                  }%, -50%) rotate(${element.rotation || 0}deg)`,
                  transformOrigin: 'center',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  zIndex: isSelected ? 10 : element.visible ? 1 : 0,
                  opacity: element.visible ? 1 : 0,
                  outline: isSelected ? '2px dashed #1976d2' : 'none',
                  outlineOffset: '4px',
                  willChange: isDragging ? 'transform' : 'auto',
                  pointerEvents: 'auto',
                  // Prevent text wrapping by ensuring container doesn't get constrained
                  width: 'max-content',
                  maxWidth: 'none',
                }}
                onMouseDown={(e) => handleMouseDown(e, element.id)}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  onSelectElement(element.id);
                  setEditingId(element.id);
                }}
              >
                <span
                  contentEditable={editingId === element.id}
                  suppressContentEditableWarning
                  ref={(el) => {
                    if (el && editingId === element.id && document.activeElement !== el) {
                      el.focus();
                      // Select all the text on enter-edit so typing replaces it
                      const range = document.createRange();
                      range.selectNodeContents(el);
                      const sel = window.getSelection();
                      sel.removeAllRanges();
                      sel.addRange(range);
                    }
                  }}
                  onBlur={(e) => {
                    if (editingId !== element.id) return;
                    const newText = e.currentTarget.innerText;
                    onElementUpdate(element.id, { text: newText });
                    setEditingId(null);
                  }}
                  onKeyDown={(e) => {
                    if (editingId !== element.id) return;
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      e.currentTarget.blur();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      // Revert by re-rendering original text
                      e.currentTarget.innerText = element.text || previewText;
                      setEditingId(null);
                    }
                  }}
                  onMouseDown={(e) => {
                    if (editingId === element.id) e.stopPropagation();
                  }}
                  style={{
                  // Fixed canvas is 540px (half of 1080p), so calculate based on that
                  fontSize: `${element.fontSize * 540 / 100}px`,
                  color: element.color,
                  opacity: element.opacity,
                  fontFamily: `${element.fontFamily || 'Arial'}, sans-serif`,
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                  whiteSpace: element.text && element.text.includes('\n') ? 'pre-line' : 'nowrap',
                  display: 'inline-block',
                  margin: 0,
                  padding: 0,
                  lineHeight: 1.2,
                  letterSpacing: 'normal',
                  fontWeight: 'normal',
                  fontStyle: 'normal',
                  textRendering: 'geometricPrecision',
                  // Prevent text from jumping during drag
                  boxSizing: 'border-box',
                  contain: 'layout style',
                  cursor: editingId === element.id ? 'text' : 'inherit',
                  outline: editingId === element.id ? '1px solid #1976d2' : 'none',
                }}>
                  {element.text || previewText}
                </span>
              </WatermarkText>
            </React.Fragment>
          );
        })}
      </CanvasContent>
    </CanvasContainer>
    </CanvasWrapper>
  );
}

export default WatermarkCanvas;